import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { forkJoin } from 'rxjs';
import { SysManagementService, SystemSettings, CalendarDay, Holiday, ReminderMonth, Reminder } from '../../services/sys-management.service';

@Component({
  selector: 'app-sys-system',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './sys-system.html',
  styleUrl: './sys-system.scss'
})
export class SysSystemComponent implements OnInit {

  // 入力範囲の設定
  currentYear: number = new Date().getFullYear();
  minYear: number = this.currentYear;
  maxYear: number = this.currentYear + 2;

  // システム設定のプロパティ
  alertMailAddress: string = '';
  supportMailAddress: string = '';
  defaultPassword: string = '';
  passwordExpireDay: number = 60;
  passwordExpireWarningDay: number = 7;

  systemSettingsLoading: boolean = false;
  systemSettingsError: string = '';
  systemSettingsLoaded: boolean = false;
  originalSystemSettings: any = null;

  // 祝日登録のプロパティ
  currentCalendarYear: number = new Date().getFullYear();
  currentCalendarMonth: number = new Date().getMonth() + 1;
  calendarDays: CalendarDay[] = [];
  selectedCalendarDay: CalendarDay | null = null;
  holidayYear: number = new Date().getFullYear();
  holidayYearOptions: number[] = [];
  holidayList: Holiday[] = [];
  holidayInputText: string = '';
  holidayLoading: boolean = false;
  holidayError: string = '';
  reminderList: Date[] = [];

  // リマインド日登録のプロパティ
  reminderYear: number = new Date().getFullYear();
  reminderYearOptions: number[] = [];
  reminderMonths: ReminderMonth[] = [];
  reminderInputText: string = '';
  reminderLoading: boolean = false;
  reminderError: string = '';

  constructor(private sysManagementService: SysManagementService) { }

  ngOnInit(): void {
    this.initializeHolidayYearOptions();
    this.initializeReminderYearOptions();
    this.initializeSystemSettings();
  }

  // 祝日年選択オプションを初期化
  private initializeHolidayYearOptions(): void {
    this.holidayYearOptions = [
      this.currentYear + 2,  // 再来年
      this.currentYear + 1,  // 来年
      this.currentYear,      // 今年
      this.currentYear - 1,  // 昨年
      this.currentYear - 2   // 一昨年
    ];
    this.holidayYear = this.currentYear;  // 今年を初期選択
  }

  // リマインド年選択オプションを初期化
  private initializeReminderYearOptions(): void {
    this.reminderYearOptions = [
      this.currentYear + 2,  // 再来年
      this.currentYear + 1,  // 来年
      this.currentYear,      // 今年
      this.currentYear - 1,  // 昨年
      this.currentYear - 2   // 一昨年
    ];
    this.reminderYear = this.currentYear;  // 今年を初期選択
  }

  // システム設定の初期化
  initializeSystemSettings(): void {
    this.initializeReminderMonths();

    forkJoin({
      settings: this.sysManagementService.getSystemSettings(),
      holidays: this.sysManagementService.getHolidaysByYear(this.currentYear),
      reminders: this.sysManagementService.getRemindersByYear(this.currentYear),
      calReminders: this.sysManagementService.getCalendarReminders(this.currentCalendarYear, this.currentCalendarMonth)
    }).subscribe({
      next: ({ settings, holidays, reminders, calReminders }) => {
        // システム設定
        this.alertMailAddress = settings.alertMailAddress;
        this.supportMailAddress = settings.supportMailAddress;
        this.defaultPassword = settings.defaultPassword;
        this.passwordExpireDay = settings.passwordExpireDay;
        this.passwordExpireWarningDay = settings.passwordExpireWarningDay;
        this.originalSystemSettings = { ...settings };
        this.systemSettingsLoaded = true;

        // 祝日（holidays / calendar 共用）
        this.holidayList = holidays;
        this.holidayInputText = this.generateHolidayInputText(holidays);

        // リマインド日
        this.reminderMonths = Array.from({ length: 12 }, (_, i) => ({
          month: `${i + 1}月`,
          monthNumber: i + 1,
          day1: '', day2: '', day3: '', day4: ''
        }));
        const textLines: string[] = [];
        reminders.forEach((reminder: any) => {
          const date = new Date(reminder.remindDate);
          const month = date.getMonth() + 1;
          const monthData = this.reminderMonths.find(m => m.monthNumber === month);
          if (monthData) {
            const mmdd = `${String(date.getMonth() + 1).padStart(2, '0')}/${String(date.getDate()).padStart(2, '0')}`;
            if (!monthData.day1) monthData.day1 = mmdd;
            else if (!monthData.day2) monthData.day2 = mmdd;
            else if (!monthData.day3) monthData.day3 = mmdd;
            else if (!monthData.day4) monthData.day4 = mmdd;
          }
          textLines.push(`${date.getFullYear()}/${date.getMonth() + 1}/${date.getDate()}`);
        });
        this.reminderInputText = textLines.join('\n');

        // カレンダー描画（holidays と calReminders を使い回し）
        this.reminderList = calReminders.map((r: any) => new Date(r.remindDate));
        this.generateCalendarDays();
      },
      error: () => {
        this.systemSettingsError = 'データの取得に失敗しました';
        this.generateCalendarDays();
      }
    });
  }

  // カレンダーの日付を生成
  generateCalendarDays(): void {
    const today = new Date();
    const firstDay = new Date(this.currentCalendarYear, this.currentCalendarMonth - 1, 1);
    const lastDay = new Date(this.currentCalendarYear, this.currentCalendarMonth, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    this.calendarDays = [];

    // 前月の日付を追加
    const prevMonthLastDay = new Date(this.currentCalendarYear, this.currentCalendarMonth - 1, 0).getDate();
    for (let i = startingDayOfWeek - 1; i >= 0; i--) {
      const day = prevMonthLastDay - i;
      const date = new Date(this.currentCalendarYear, this.currentCalendarMonth - 2, day);
      const dayOfWeek = date.getDay();
      this.calendarDays.push({
        day: day,
        date: date,
        isToday: false,
        isHoliday: false,
        isReminder: false,
        isOtherMonth: true,
        isSelected: false,
        isSunday: dayOfWeek === 0,
        isSaturday: dayOfWeek === 6
      });
    }

    // 当月の日付を追加
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(this.currentCalendarYear, this.currentCalendarMonth - 1, day);
      const isToday = date.toDateString() === today.toDateString();
      const dayOfWeek = date.getDay();
      this.calendarDays.push({
        day: day,
        date: date,
        isToday: isToday,
        isHoliday: this.isHolidayDate(date),
        isReminder: this.isReminderDate(date),
        isOtherMonth: false,
        isSelected: false,
        isSunday: dayOfWeek === 0,
        isSaturday: dayOfWeek === 6
      });
    }

    // 次月の日付を追加（6週間分になるように）
    const remainingDays = 42 - this.calendarDays.length;
    for (let day = 1; day <= remainingDays; day++) {
      const date = new Date(this.currentCalendarYear, this.currentCalendarMonth, day);
      const dayOfWeek = date.getDay();
      this.calendarDays.push({
        day: day,
        date: date,
        isToday: false,
        isHoliday: false,
        isReminder: false,
        isOtherMonth: true,
        isSelected: false,
        isSunday: dayOfWeek === 0,
        isSaturday: dayOfWeek === 6
      });
    }
  }

  // 祝日判定
  isHolidayDate(date: Date): boolean {
    const dateString = this.formatDate(date);
    return this.holidayList.some(holiday => {
      const holidayDateOnly = holiday.date.split('T')[0];
      return holidayDateOnly === dateString;
    });
  }

  // リマインド日判定
  isReminderDate(date: Date): boolean {
    return this.reminderList.some(reminder =>
      reminder.toDateString() === date.toDateString()
    );
  }

  // 日付をフォーマット (YYYY-MM-DD)
  formatDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  // 前月に移動
  previousMonth(): void {
    const prevYear = this.currentCalendarYear;
    this.currentCalendarMonth--;
    if (this.currentCalendarMonth < 1) {
      this.currentCalendarMonth = 12;
      this.currentCalendarYear--;
    }

    if (prevYear !== this.currentCalendarYear) {
      this.loadHolidaysForCalendar();
    } else {
      this.loadCalendarData();
    }
  }

  // 次月に移動
  nextMonth(): void {
    const prevYear = this.currentCalendarYear;
    this.currentCalendarMonth++;
    if (this.currentCalendarMonth > 12) {
      this.currentCalendarMonth = 1;
      this.currentCalendarYear++;
    }

    if (prevYear !== this.currentCalendarYear) {
      this.loadHolidaysForCalendar();
    } else {
      this.loadCalendarData();
    }
  }

  // カレンダー用祝日読み込み
  loadHolidaysForCalendar(): void {
    this.sysManagementService.getHolidaysByYear(this.currentCalendarYear).subscribe({
      next: (holidays) => {
        this.holidayList = holidays;
        this.loadCalendarReminders();
      },
      error: () => {
        this.holidayList = [];
        this.loadCalendarReminders();
      }
    });
  }

  // カレンダーデータ読み込み
  loadCalendarData(): void {
    this.loadHolidaysForCalendar();
  }

  // カレンダー用リマインド読み込み
  loadCalendarReminders(): void {
    this.sysManagementService.getCalendarReminders(this.currentCalendarYear, this.currentCalendarMonth).subscribe({
      next: (reminders: any[]) => {
        this.reminderList = reminders.map((r: any) => new Date(r.remindDate));
        this.generateCalendarDays();
      },
      error: () => {
        this.reminderList = [];
        this.generateCalendarDays();
      }
    });
  }

  // リマインド月の初期化
  initializeReminderMonths(): void {
    this.reminderMonths = [
      { month: '1', monthNumber: 1, day1: '', day2: '', day3: '', day4: '' },
      { month: '2', monthNumber: 2, day1: '', day2: '', day3: '', day4: '' },
      { month: '3', monthNumber: 3, day1: '', day2: '', day3: '', day4: '' },
      { month: '4', monthNumber: 4, day1: '', day2: '', day3: '', day4: '' },
      { month: '5', monthNumber: 5, day1: '', day2: '', day3: '', day4: '' },
      { month: '6', monthNumber: 6, day1: '', day2: '', day3: '', day4: '' },
      { month: '7', monthNumber: 7, day1: '', day2: '', day3: '', day4: '' },
      { month: '8', monthNumber: 8, day1: '', day2: '', day3: '', day4: '' },
      { month: '9', monthNumber: 9, day1: '', day2: '', day3: '', day4: '' },
      { month: '10', monthNumber: 10, day1: '', day2: '', day3: '', day4: '' },
      { month: '11', monthNumber: 11, day1: '', day2: '', day3: '', day4: '' },
      { month: '12', monthNumber: 12, day1: '', day2: '', day3: '', day4: '' }
    ];
  }

  // システム設定を読み込み
  loadSystemSettings(): void {
    if (this.systemSettingsLoaded) {
      return;
    }

    this.systemSettingsLoading = true;
    this.systemSettingsError = '';

    this.sysManagementService.getSystemSettings().subscribe({
      next: (settings) => {
        this.alertMailAddress = settings.alertMailAddress;
        this.supportMailAddress = settings.supportMailAddress;
        this.defaultPassword = settings.defaultPassword;
        this.passwordExpireDay = settings.passwordExpireDay;
        this.passwordExpireWarningDay = settings.passwordExpireWarningDay;

        this.originalSystemSettings = { ...settings };

        this.systemSettingsLoaded = true;
        this.systemSettingsLoading = false;
      },
      error: () => {
        this.systemSettingsError = '';
        this.systemSettingsLoading = false;
      }
    });
  }

  // システム設定を保存
  saveSystemSettings(): void {
    if (!this.alertMailAddress || !this.supportMailAddress) {
      alert('アラート送信先を入力してください');
      return;
    }

    if (!this.defaultPassword) {
      alert('初期パスワードを入力してください');
      return;
    }

    if (this.passwordExpireDay < 1 || this.passwordExpireWarningDay < 1) {
      alert('パスワード有効期間と予告日は1日以上を指定してください');
      return;
    }

    const settings: SystemSettings = {
      alertMailAddress: this.alertMailAddress,
      supportMailAddress: this.supportMailAddress,
      defaultPassword: this.defaultPassword,
      passwordExpireDay: this.passwordExpireDay,
      passwordExpireWarningDay: this.passwordExpireWarningDay
    };

    this.systemSettingsLoading = true;
    this.systemSettingsError = '';

    this.sysManagementService.saveSystemSettings(settings).subscribe({
      next: (result) => {
        alert('システム設定を保存しました');
        this.originalSystemSettings = { ...settings };
        this.systemSettingsLoading = false;
      },
      error: () => {
        this.systemSettingsError = 'システム設定の保存に失敗しました';
        alert('システム設定の保存に失敗しました');
        this.systemSettingsLoading = false;
      }
    });
  }

  // システム設定をリセット
  resetSystemSettings(): void {
    if (this.originalSystemSettings) {
      this.alertMailAddress = this.originalSystemSettings.alertMailAddress;
      this.supportMailAddress = this.originalSystemSettings.supportMailAddress;
      this.defaultPassword = this.originalSystemSettings.defaultPassword;
      this.passwordExpireDay = this.originalSystemSettings.passwordExpireDay;
      this.passwordExpireWarningDay = this.originalSystemSettings.passwordExpireWarningDay;
    } else {
      this.loadSystemSettings();
    }
  }

  // 祝日を読み込み
  loadHolidays(): void {
    this.holidayLoading = true;
    this.holidayError = '';

    this.sysManagementService.getHolidaysByYear(this.holidayYear).subscribe({
      next: (holidays) => {
        this.holidayList = holidays;
        this.holidayInputText = this.generateHolidayInputText(holidays);

        if (this.currentCalendarYear === this.holidayYear && this.calendarDays.length > 0) {
          this.generateCalendarDays();
        }

        this.holidayLoading = false;
      },
      error: () => {
        this.holidayError = '';
        this.holidayList = [];
        this.holidayInputText = '';
        this.holidayLoading = false;
      }
    });
  }

  // 祝日入力テキスト生成
  private generateHolidayInputText(holidays: Holiday[]): string {
    return holidays.map(h => {
      const date = new Date(h.date);
      const year = date.getFullYear();
      const month = date.getMonth() + 1;
      const day = date.getDate();
      return `${year}/${month}/${day}`;
    }).join('\n');
  }

  // 祝日日付フォーマット
  formatHolidayDate(dateString: string): string {
    const date = new Date(dateString);
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${month}/${day}`;
  }

  // 祝日年変更時
  onHolidayYearChange(): void {
    this.loadHolidays();
  }

  // 祝日を保存
  saveHolidays(): void {
    const lines = this.holidayInputText.split('\n').filter(line => line.trim() !== '');
    const newHolidays: Holiday[] = [];
    const uniqueDates = new Set<string>();

    for (const line of lines) {
      const trimmedLine = line.trim();
      if (trimmedLine === '') continue;

      const dateMatch = trimmedLine.match(/^(\d{4})\/(\d{1,2})\/(\d{1,2})$/);
      if (!dateMatch) {
        alert('日付の形式が正しくありません。YYYY/M/D の形式で入力してください。');
        return;
      }

      const year = parseInt(dateMatch[1], 10);
      const month = parseInt(dateMatch[2], 10);
      const day = parseInt(dateMatch[3], 10);

      if (year !== this.holidayYear) {
        alert(`${this.holidayYear}年以外の日付が含まれています: ${year}年`);
        return;
      }

      const date = new Date(year, month - 1, day);
      if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
        alert(`無効な日付です: ${year}/${month}/${day}`);
        return;
      }

      const formattedDate = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

      if (!uniqueDates.has(formattedDate)) {
        uniqueDates.add(formattedDate);
        newHolidays.push({ date: formattedDate });
      }
    }

    const confirmMessage = `${this.holidayYear}年の祝日を保存します。\n既存のデータは削除され、${newHolidays.length}件の祝日が登録されます。\nよろしいですか?`;
    if (!confirm(confirmMessage)) {
      return;
    }

    this.holidayLoading = true;
    this.holidayError = '';

    this.sysManagementService.saveHolidays(this.holidayYear, newHolidays).subscribe({
      next: () => {
        alert(`${this.holidayYear}年の祝日を保存しました (${newHolidays.length}件)`);
        this.loadHolidays();
        this.holidayLoading = false;
      },
      error: () => {
        this.holidayError = '祝日の保存に失敗しました';
        alert('祝日の保存に失敗しました');
        this.holidayLoading = false;
      }
    });
  }

  // リマインド日を読み込み
  loadReminders(): void {
    this.reminderLoading = true;
    this.reminderError = '';

    this.sysManagementService.getRemindersByYear(this.reminderYear).subscribe({
      next: (reminders: any[]) => {
        this.reminderMonths = Array.from({ length: 12 }, (_, i) => ({
          month: `${i + 1}月`,
          monthNumber: i + 1,
          day1: '',
          day2: '',
          day3: '',
          day4: ''
        }));

        const textLines: string[] = [];

        reminders.forEach((reminder: any) => {
          const date = new Date(reminder.remindDate);
          const month = date.getMonth() + 1;
          const monthData = this.reminderMonths.find(m => m.monthNumber === month);

          if (monthData) {
            const mmdd = `${String(date.getMonth() + 1).padStart(2, '0')}/${String(date.getDate()).padStart(2, '0')}`;

            if (!monthData.day1) monthData.day1 = mmdd;
            else if (!monthData.day2) monthData.day2 = mmdd;
            else if (!monthData.day3) monthData.day3 = mmdd;
            else if (!monthData.day4) monthData.day4 = mmdd;
          }

          textLines.push(`${date.getFullYear()}/${date.getMonth() + 1}/${date.getDate()}`);
        });

        this.reminderInputText = textLines.join('\n');

        if (this.currentCalendarYear === this.reminderYear && this.calendarDays.length > 0) {
          this.reminderList = reminders.map((r: any) => new Date(r.remindDate));
          this.generateCalendarDays();
        }

        this.reminderLoading = false;
      },
      error: () => {
        this.reminderError = '';
        this.reminderLoading = false;
      }
    });
  }

  // リマインド年変更時
  onReminderYearChange(): void {
    this.loadReminders();
  }

  // リマインド日を更新
  updateReminders(): void {
    const reminders: Reminder[] = [];

    this.reminderMonths.forEach(monthData => {
      [monthData.day1, monthData.day2, monthData.day3, monthData.day4].forEach(dayStr => {
        if (dayStr && dayStr.trim() !== '') {
          const parts = dayStr.trim().split('/');
          if (parts.length === 2) {
            const month = parseInt(parts[0], 10);
            const day = parseInt(parts[1], 10);

            const date = new Date(this.reminderYear, month - 1, day);
            if (date.getFullYear() === this.reminderYear &&
              date.getMonth() === month - 1 &&
              date.getDate() === day) {

              const formattedDate = `${this.reminderYear}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
              reminders.push({ remindDate: formattedDate });
            } else {
              alert('無効な日付が含まれています');
              return;
            }
          }
        }
      });
    });

    this.saveRemindersToServer(reminders, 'update');
  }

  // リマインド日を登録
  saveReminders(): void {
    const lines = this.reminderInputText.split('\n').filter(line => line.trim() !== '');
    const newReminders: Reminder[] = [];
    const uniqueDates = new Set<string>();

    for (const line of lines) {
      const trimmedLine = line.trim();
      if (trimmedLine === '') continue;

      const dateMatch = trimmedLine.match(/^(\d{4})\/(\d{1,2})\/(\d{1,2})$/);
      if (!dateMatch) {
        alert('日付の形式が正しくありません。YYYY/M/D の形式で入力してください。');
        return;
      }

      const year = parseInt(dateMatch[1], 10);
      const month = parseInt(dateMatch[2], 10);
      const day = parseInt(dateMatch[3], 10);

      if (year !== this.reminderYear) {
        alert(`${this.reminderYear}年以外の日付が含まれています: ${year}年`);
        return;
      }

      const date = new Date(year, month - 1, day);
      if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
        alert(`無効な日付です: ${year}/${month}/${day}`);
        return;
      }

      const formattedDate = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

      if (!uniqueDates.has(formattedDate)) {
        uniqueDates.add(formattedDate);
        newReminders.push({ remindDate: formattedDate });
      }
    }

    this.saveRemindersToServer(newReminders, 'register');
  }

  // リマインド日をサーバーに保存
  private saveRemindersToServer(reminders: Reminder[], mode: 'update' | 'register'): void {
    const modeText = mode === 'update' ? '更新' : '登録';

    const confirmMessage = `${this.reminderYear}年のリマインド日を${modeText}します。\n既存のデータは削除され、${reminders.length}件のリマインド日が登録されます。\nよろしいですか?`;
    if (!confirm(confirmMessage)) {
      return;
    }

    this.reminderLoading = true;
    this.reminderError = '';

    this.sysManagementService.saveReminders(this.reminderYear, reminders).subscribe({
      next: () => {
        alert(`${this.reminderYear}年のリマインド日を${modeText}しました (${reminders.length}件)`);
        this.loadReminders();
      },
      error: () => {
        this.reminderError = `リマインド日の${modeText}に失敗しました`;
        alert(`リマインド日の${modeText}に失敗しました`);
        this.reminderLoading = false;
      }
    });
  }

}
